import {
   IStage,
   IStageMapGroup,
   TStageGroupId,
   TStopStageKey,
   TStageGroupIdentifier,
} from '../interfaces/stage-mapping.types'

/**
 * Result type for extracting stages and tags
 */
export interface IExtractedStagesAndTags {
   stages: IStage[]
   tags: IStage[]
}

/**
 * Define top-level group IDs
 */
const TOP_LEVEL_GROUP_IDS: TStageGroupId[] = [
   'ff-nurturing',
   'ff-appointment-set',
   'ff-appointment-missed',
   'ff-optional-mappings',
]

/**
 * Define nested keys within 'ff-stopped'
 */
const NESTED_STOP_KEYS: TStopStageKey[] = [
   'ff-active-client',
   'ff-under-contract',
   'ff-closed-client',
   'ff-do-not-contact',
   'ff-has-agent',
   'ff-other-stop',
]

/**
 * Check if identifier is a top-level group
 */
export const isTopLevelGroup = (identifier: TStageGroupIdentifier): boolean => {
   return TOP_LEVEL_GROUP_IDS.includes(identifier as TStageGroupId)
}

/**
 * Check if identifier is a nested stop key
 */
export const isNestedStopKey = (identifier: TStageGroupIdentifier): boolean => {
   return NESTED_STOP_KEYS.includes(identifier as TStopStageKey)
}

/**
 * Extracts stages and tags from stage mapping groups by group ID or key
 *
 * @param identifier - The group ID or stop stage key to extract data from
 * @param stageMappingGroups - The full list of stage mapping groups
 * @returns Object containing stages and tags arrays, or empty arrays if not found
 *
 * @example
 * // Extract from top-level group
 * const { stages, tags } = extractStagesAndTagsByGroup('ff-appointment-set', stageMappingGroups)
 *
 * @example
 * // Extract from nested group
 * const { stages, tags } = extractStagesAndTagsByGroup('ff-active-client', stageMappingGroups)
 */
export const extractStagesAndTagsByGroup = (
   identifier: TStageGroupIdentifier,
   stageMappingGroups: IStageMapGroup[]
): IExtractedStagesAndTags => {
   // Check if identifier is a top-level group
   if (isTopLevelGroup(identifier)) {
      const group = stageMappingGroups.find((g) => g.group === identifier)
      return {
         stages: group?.stages || [],
         tags: group?.tags || [],
      }
   }

   // Check if identifier is a nested key within 'ff-stopped'
   if (isNestedStopKey(identifier)) {
      const stoppedGroup = stageMappingGroups.find(
         (g) => g.group === 'ff-stopped'
      )
      const subGroup = stoppedGroup?.types?.find((t) => t.key === identifier)
      return {
         stages: subGroup?.stages || [],
         tags: subGroup?.tags || [],
      }
   }

   // If identifier is 'ff-stopped' itself, return all stages and tags from all sub-groups
   if (identifier === 'ff-stopped') {
      const stoppedGroup = stageMappingGroups.find(
         (g) => g.group === 'ff-stopped'
      )
      const allStages = stoppedGroup?.types?.flatMap((t) => t.stages) || []
      const allTags = stoppedGroup?.types?.flatMap((t) => t.tags) || []
      return {
         stages: allStages,
         tags: allTags,
      }
   }

   // Return empty arrays if identifier is not found
   return {
      stages: [],
      tags: [],
   }
}

/**
 * Add a stage to a specific group
 * @param groups - Current stage mapping groups
 * @param stage - Stage to add
 * @param identifier - Target group identifier
 * @param timeline - Optional timeline for nurturing groups
 * @returns Updated groups array
 */
export const addStageToGroupUtil = (
   groups: IStageMapGroup[],
   stage: IStage,
   identifier: TStageGroupIdentifier,
   timeline?: string
): IStageMapGroup[] => {
   const newGroups = [...groups]

   if (isTopLevelGroup(identifier)) {
      // For ff-nurturing, use timeline to find the specific group
      if (identifier === 'ff-nurturing' && timeline) {
         const groupIndex = newGroups.findIndex(
            (g) => g.group === identifier && g.timeline === timeline
         )
         if (groupIndex !== -1) {
            const existingStages = newGroups[groupIndex].stages || []
            if (!existingStages.some((s) => s.id === stage.id)) {
               newGroups[groupIndex] = {
                  ...newGroups[groupIndex],
                  stages: [...existingStages, stage],
               }
            }
         }
      } else {
         // For other groups, find by identifier only
         const groupIndex = newGroups.findIndex((g) => g.group === identifier)
         if (groupIndex !== -1) {
            const existingStages = newGroups[groupIndex].stages || []
            if (!existingStages.some((s) => s.id === stage.id)) {
               newGroups[groupIndex] = {
                  ...newGroups[groupIndex],
                  stages: [...existingStages, stage],
               }
            }
         }
      }
   } else if (isNestedStopKey(identifier)) {
      const stoppedGroupIndex = newGroups.findIndex(
         (g) => g.group === 'ff-stopped'
      )
      if (stoppedGroupIndex !== -1) {
         const types = newGroups[stoppedGroupIndex].types || []
         const subGroupIndex = types.findIndex((t) => t.key === identifier)
         if (subGroupIndex !== -1) {
            const existingStages = types[subGroupIndex].stages || []
            if (!existingStages.some((s) => s.id === stage.id)) {
               types[subGroupIndex] = {
                  ...types[subGroupIndex],
                  stages: [...existingStages, stage],
               }
               newGroups[stoppedGroupIndex] = {
                  ...newGroups[stoppedGroupIndex],
                  types: [...types],
               }
            }
         }
      }
   }

   return newGroups
}

/**
 * Remove a stage from a specific group
 * @param groups - Current stage mapping groups
 * @param stageId - ID of stage to remove
 * @param identifier - Target group identifier
 * @param timeline - Optional timeline for nurturing groups
 * @returns Updated groups array
 */
export const removeStageFromGroupUtil = (
   groups: IStageMapGroup[],
   stageId: string,
   identifier: TStageGroupIdentifier,
   timeline?: string
): IStageMapGroup[] => {
   const newGroups = [...groups]

   if (isTopLevelGroup(identifier)) {
      // For ff-nurturing, use timeline to find the specific group
      if (identifier === 'ff-nurturing' && timeline) {
         const groupIndex = newGroups.findIndex(
            (g) => g.group === identifier && g.timeline === timeline
         )
         if (groupIndex !== -1) {
            newGroups[groupIndex] = {
               ...newGroups[groupIndex],
               stages: (newGroups[groupIndex].stages || []).filter(
                  (s) => s.id !== stageId
               ),
            }
         }
      } else {
         // For other groups, find by identifier only
         const groupIndex = newGroups.findIndex((g) => g.group === identifier)
         if (groupIndex !== -1) {
            newGroups[groupIndex] = {
               ...newGroups[groupIndex],
               stages: (newGroups[groupIndex].stages || []).filter(
                  (s) => s.id !== stageId
               ),
            }
         }
      }
   } else if (isNestedStopKey(identifier)) {
      const stoppedGroupIndex = newGroups.findIndex(
         (g) => g.group === 'ff-stopped'
      )
      if (stoppedGroupIndex !== -1) {
         const types = newGroups[stoppedGroupIndex].types || []
         const subGroupIndex = types.findIndex((t) => t.key === identifier)
         if (subGroupIndex !== -1) {
            types[subGroupIndex] = {
               ...types[subGroupIndex],
               stages: (types[subGroupIndex].stages || []).filter(
                  (s) => s.id !== stageId
               ),
            }
            newGroups[stoppedGroupIndex] = {
               ...newGroups[stoppedGroupIndex],
               types: [...types],
            }
         }
      }
   }

   return newGroups
}

/**
 * Add a tag to a specific group
 * @param groups - Current stage mapping groups
 * @param tag - Tag to add
 * @param identifier - Target group identifier
 * @param timeline - Optional timeline for nurturing groups
 * @returns Updated groups array
 */
export const addTagToGroupUtil = (
   groups: IStageMapGroup[],
   tag: IStage,
   identifier: TStageGroupIdentifier,
   timeline?: string
): IStageMapGroup[] => {
   const newGroups = [...groups]

   if (isTopLevelGroup(identifier)) {
      // For ff-nurturing, use timeline to find the specific group
      if (identifier === 'ff-nurturing' && timeline) {
         const groupIndex = newGroups.findIndex(
            (g) => g.group === identifier && g.timeline === timeline
         )
         if (groupIndex !== -1) {
            const existingTags = newGroups[groupIndex].tags || []
            if (!existingTags.some((t) => t.id === tag.id)) {
               newGroups[groupIndex] = {
                  ...newGroups[groupIndex],
                  tags: [...existingTags, tag],
               }
            }
         }
      } else {
         // For other groups, find by identifier only
         const groupIndex = newGroups.findIndex((g) => g.group === identifier)
         if (groupIndex !== -1) {
            const existingTags = newGroups[groupIndex].tags || []
            if (!existingTags.some((t) => t.id === tag.id)) {
               newGroups[groupIndex] = {
                  ...newGroups[groupIndex],
                  tags: [...existingTags, tag],
               }
            }
         }
      }
   } else if (isNestedStopKey(identifier)) {
      const stoppedGroupIndex = newGroups.findIndex(
         (g) => g.group === 'ff-stopped'
      )
      if (stoppedGroupIndex !== -1) {
         const types = newGroups[stoppedGroupIndex].types || []
         const subGroupIndex = types.findIndex((t) => t.key === identifier)
         if (subGroupIndex !== -1) {
            const existingTags = types[subGroupIndex].tags || []
            if (!existingTags.some((t) => t.id === tag.id)) {
               types[subGroupIndex] = {
                  ...types[subGroupIndex],
                  tags: [...existingTags, tag],
               }
               newGroups[stoppedGroupIndex] = {
                  ...newGroups[stoppedGroupIndex],
                  types: [...types],
               }
            }
         }
      }
   }

   return newGroups
}

/**
 * Remove a tag from a specific group
 * @param groups - Current stage mapping groups
 * @param tagId - ID of tag to remove
 * @param identifier - Target group identifier
 * @param timeline - Optional timeline for nurturing groups
 * @returns Updated groups array
 */
export const removeTagFromGroupUtil = (
   groups: IStageMapGroup[],
   tagId: string,
   identifier: TStageGroupIdentifier,
   timeline?: string
): IStageMapGroup[] => {
   const newGroups = [...groups]

   if (isTopLevelGroup(identifier)) {
      // For ff-nurturing, use timeline to find the specific group
      if (identifier === 'ff-nurturing' && timeline) {
         const groupIndex = newGroups.findIndex(
            (g) => g.group === identifier && g.timeline === timeline
         )
         if (groupIndex !== -1) {
            newGroups[groupIndex] = {
               ...newGroups[groupIndex],
               tags: (newGroups[groupIndex].tags || []).filter(
                  (t) => t.id !== tagId
               ),
            }
         }
      } else {
         // For other groups, find by identifier only
         const groupIndex = newGroups.findIndex((g) => g.group === identifier)
         if (groupIndex !== -1) {
            newGroups[groupIndex] = {
               ...newGroups[groupIndex],
               tags: (newGroups[groupIndex].tags || []).filter(
                  (t) => t.id !== tagId
               ),
            }
         }
      }
   } else if (isNestedStopKey(identifier)) {
      const stoppedGroupIndex = newGroups.findIndex(
         (g) => g.group === 'ff-stopped'
      )
      if (stoppedGroupIndex !== -1) {
         const types = newGroups[stoppedGroupIndex].types || []
         const subGroupIndex = types.findIndex((t) => t.key === identifier)
         if (subGroupIndex !== -1) {
            types[subGroupIndex] = {
               ...types[subGroupIndex],
               tags: (types[subGroupIndex].tags || []).filter(
                  (t) => t.id !== tagId
               ),
            }
            newGroups[stoppedGroupIndex] = {
               ...newGroups[stoppedGroupIndex],
               types: [...types],
            }
         }
      }
   }

   return newGroups
}

/**
 * Remove a stage from all groups
 * @param groups - Current stage mapping groups
 * @param stageId - ID of stage to remove
 * @returns Updated groups array
 */
export const removeStageFromAllGroupsUtil = (
   groups: IStageMapGroup[],
   stageId: string
): IStageMapGroup[] => {
   return groups.map((group) => {
      // Remove from top-level stages
      const filteredStages = (group.stages || []).filter(
         (s) => s.id !== stageId
      )

      // Remove from nested sub-groups
      const filteredTypes = group.types?.map((type) => ({
         ...type,
         stages: (type.stages || []).filter((s) => s.id !== stageId),
      }))

      return {
         ...group,
         stages: filteredStages,
         types: filteredTypes,
      }
   })
}

/**
 * Remove a tag from all groups
 * @param groups - Current stage mapping groups
 * @param tagId - ID of tag to remove
 * @returns Updated groups array
 */
export const removeTagFromAllGroupsUtil = (
   groups: IStageMapGroup[],
   tagId: string
): IStageMapGroup[] => {
   return groups.map((group) => {
      // Remove from top-level tags
      const filteredTags = (group.tags || []).filter((t) => t.id !== tagId)

      // Remove from nested sub-groups
      const filteredTypes = group.types?.map((type) => ({
         ...type,
         tags: (type.tags || []).filter((t) => t.id !== tagId),
      }))

      return {
         ...group,
         tags: filteredTags,
         types: filteredTypes,
      }
   })
}

/**
 * Get all nurturing groups
 * @param groups - Current stage mapping groups
 * @returns Array of nurturing groups
 */
export const getNurturingGroupsUtil = (
   groups: IStageMapGroup[]
): IStageMapGroup[] => {
   return groups.filter((g) => g.group === 'ff-nurturing')
}

/**
 * Get stages and tags by timeline for nurturing groups
 * @param groups - Current stage mapping groups
 * @param timeline - Timeline to filter by
 * @returns Object containing stages and tags arrays
 */
export const getStagesAndTagsByTimelineUtil = (
   groups: IStageMapGroup[],
   timeline: string
): IExtractedStagesAndTags => {
   const nurturingGroup = groups.find(
      (g) => g.group === 'ff-nurturing' && g.timeline === timeline
   )
   return {
      stages: nurturingGroup?.stages || [],
      tags: nurturingGroup?.tags || [],
   }
}

/**
 * Add a new nurturing group with the specified timeline
 * @param groups - Current stage mapping groups
 * @param timeline - Timeline for the new nurturing group
 * @returns Updated groups array
 */
export const addNurturingGroupUtil = (
   groups: IStageMapGroup[],
   timeline: string
): IStageMapGroup[] => {
   // Check if a nurturing group with this timeline already exists
   const exists = groups.some(
      (g) => g.group === 'ff-nurturing' && g.timeline === timeline
   )

   if (exists) {
      return groups // Don't add duplicate
   }

   // Create new nurturing group
   const newNurturingGroup: IStageMapGroup = {
      group: 'ff-nurturing',
      timeline: timeline,
      isMandatory: true,
      allowMultiple: false,
      stages: [],
      tags: [],
   }

   // Add after the last nurturing group
   const lastNurturingIndex = groups
      .map((g, i) => (g.group === 'ff-nurturing' ? i : -1))
      .filter((i) => i !== -1)
      .pop()

   if (lastNurturingIndex !== undefined) {
      // Insert after the last nurturing group
      return [
         ...groups.slice(0, lastNurturingIndex + 1),
         newNurturingGroup,
         ...groups.slice(lastNurturingIndex + 1),
      ]
   } else {
      // No nurturing groups exist, add at the beginning
      return [newNurturingGroup, ...groups]
   }
}

/**
 * Remove a nurturing group with the specified timeline
 * @param groups - Current stage mapping groups
 * @param timeline - Timeline of the nurturing group to remove
 * @returns Updated groups array
 */
export const removeNurturingGroupUtil = (
   groups: IStageMapGroup[],
   timeline: string
): IStageMapGroup[] => {
   return groups.filter(
      (g) => !(g.group === 'ff-nurturing' && (g.timeline || '') === timeline)
   )
}

/**
 * Update a nurturing group's timeline
 * @param groups - Current stage mapping groups
 * @param oldTimeline - Current timeline of the nurturing group
 * @param newTimeline - New timeline to set
 * @returns Updated groups array
 */
export const updateNurturingGroupTimelineUtil = (
   groups: IStageMapGroup[],
   oldTimeline: string,
   newTimeline: string
): IStageMapGroup[] => {
   // Check if new timeline already exists (and it's not the same group)
   const exists = groups.some(
      (g) =>
         g.group === 'ff-nurturing' &&
         g.timeline === newTimeline &&
         (g.timeline || '') !== oldTimeline
   )
   if (exists) {
      return groups // Don't update if new timeline already exists
   }

   return groups.map((g) => {
      if (g.group === 'ff-nurturing' && (g.timeline || '') === oldTimeline) {
         return {
            ...g,
            timeline: newTimeline,
            isMandatory: g.isMandatory ?? true,
            allowMultiple: g.allowMultiple ?? false,
         }
      }
      return g
   })
}

/**
 * Check if a group/subgroup allows multiple items
 * @param groups - Current stage mapping groups
 * @param identifier - Group identifier
 * @param timeline - Optional timeline for nurturing groups
 * @returns boolean indicating if multiple items are allowed
 */
export const getAllowMultiple = (
   groups: IStageMapGroup[],
   identifier: TStageGroupIdentifier,
   timeline?: string
): boolean => {
   if (isTopLevelGroup(identifier)) {
      if (identifier === 'ff-nurturing' && timeline) {
         const group = groups.find(
            (g) => g.group === identifier && g.timeline === timeline
         )
         return group?.allowMultiple !== false // default to true if undefined
      } else {
         const group = groups.find((g) => g.group === identifier)
         return group?.allowMultiple !== false // default to true if undefined
      }
   } else if (isNestedStopKey(identifier)) {
      const stoppedGroup = groups.find((g) => g.group === 'ff-stopped')
      const subGroup = stoppedGroup?.types?.find((t) => t.key === identifier)
      return subGroup?.allowMultiple !== false // default to true if undefined
   }
   return true // default to allowing multiple
}

/**
 * Check if an item can be added to a group
 * @param groups - Current stage mapping groups
 * @param identifier - Group identifier
 * @param isTag - Whether the item is a tag
 * @param timeline - Optional timeline for nurturing groups
 * @returns boolean indicating if item can be added
 */
export const canAddItem = (
   _groups: IStageMapGroup[],
   _identifier: TStageGroupIdentifier,
   _isTag?: boolean,
   _timeline?: string
): boolean => {
   return true
}
